// Stream SSE de um chat session (GET /api/chat/sessions/:id/stream).
// Backfill separado via chatApi.getMessages — tudo converge no mesmo buffer,
// deduplicado por id e ordenado por createdAt.
import { useEffect, useRef, useState, useCallback } from 'react';
import type { ApiChatMessage } from '../types/chat';
import { logger } from '../utils/logger';

export const useSessionStream = (sessionId: string | null) => {
  const [messages, setMessages] = useState<ApiChatMessage[]>([]);
  const [connected, setConnected] = useState(false);
  const seenIds = useRef<Set<string>>(new Set());

  const mergeMessages = useCallback((incoming: ApiChatMessage[]) => {
    const fresh = incoming.filter(m => m?.id && !seenIds.current.has(m.id));
    if (fresh.length === 0) return;
    fresh.forEach(m => seenIds.current.add(m.id));
    setMessages(prev => {
      const next = [...prev, ...fresh];
      next.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      return next;
    });
  }, []);

  useEffect(() => {
    seenIds.current.clear();
    setMessages([]);
    setConnected(false);
    if (!sessionId) return;

    // same-origin via proxy do Vite (/api → :4000) — cookies fluem sozinhos
    const eventSource = new EventSource(`/api/chat/sessions/${sessionId}/stream`);

    eventSource.onopen = () => setConnected(true);
    eventSource.onerror = () => setConnected(false); // EventSource reconecta nativamente
    eventSource.onmessage = (event: MessageEvent<string>) => {
      try {
        // Frame novo: {"type":"message","message":{...}} — c/ fallback p/ msg direta
        const payload = JSON.parse(event.data) as {
          type?: string;
          message?: ApiChatMessage;
        } & Partial<ApiChatMessage>;
        const message: ApiChatMessage | null =
          payload.type === 'message' && payload.message
            ? payload.message
            : payload.id && payload.content
              ? (payload as ApiChatMessage)
              : null;
        if (message?.id) {
          mergeMessages([message]);
        }
      } catch (err) {
        logger.error('SSE: evento inválido', err);
      }
    };

    return () => {
      eventSource.close();
      setConnected(false);
    };
  }, [sessionId, mergeMessages]);

  return { messages, connected, mergeMessages };
};